import type { DetectionBox, DetectionResult, Detector } from './Detector';

interface PendingEntry {
  resolve: (value: unknown) => void;
  reject: (err: Error) => void;
}

interface WorkerResponse {
  reqId: number;
  ok: boolean;
  error?: string;
  detections?: DetectionBox[];
  inferMs?: number;
}

export interface CocoSsdWorkerOptions {
  base?: 'lite_mobilenet_v2' | 'mobilenet_v2' | 'mobilenet_v1';
  minScore?: number;
  maxDetections?: number;
}

export class CocoSsdDetectorWorker implements Detector {
  readonly id = 'coco-ssd-worker';
  readonly label: string;
  private worker: Worker | null = null;
  private reqId = 0;
  private readonly pending = new Map<number, PendingEntry>();

  constructor(private readonly opts: CocoSsdWorkerOptions = {}) {
    this.label = `COCO-SSD worker (${opts.base ?? 'lite_mobilenet_v2'})`;
  }

  async initialize(): Promise<void> {
    if (this.worker) return;
    this.worker = new Worker(new URL('../../workers/detection.worker.ts', import.meta.url), {
      type: 'module',
    });
    this.worker.onmessage = (ev: MessageEvent<WorkerResponse>) => this.handleMessage(ev.data);
    this.worker.onerror = (err) => {
      console.error('[CocoSsdDetectorWorker] worker error', err);
      for (const entry of this.pending.values()) entry.reject(new Error('worker error'));
      this.pending.clear();
    };
    await this.request({ type: 'init', base: this.opts.base });
  }

  async detect(source: HTMLVideoElement): Promise<DetectionResult> {
    if (!this.worker) throw new Error('CocoSsdDetectorWorker not initialized');
    if (source.videoWidth <= 0 || source.videoHeight <= 0) {
      return { detections: [], inferenceTimeMs: 0 };
    }
    const bitmap = await createImageBitmap(source);
    const result = (await this.request(
      {
        type: 'detect',
        bitmap,
        minScore: this.opts.minScore,
        maxDetections: this.opts.maxDetections,
      },
      [bitmap],
    )) as { detections: DetectionBox[]; inferMs: number };
    return { detections: result.detections, inferenceTimeMs: result.inferMs };
  }

  dispose(): void {
    if (!this.worker) return;
    const w = this.worker;
    this.worker = null;
    for (const entry of this.pending.values()) entry.reject(new Error('disposed'));
    this.pending.clear();
    try {
      w.postMessage({ type: 'dispose', reqId: 0 });
    } catch {
      // ignore
    }
    w.terminate();
  }

  private handleMessage(data: WorkerResponse): void {
    const entry = this.pending.get(data.reqId);
    if (!entry) return;
    this.pending.delete(data.reqId);
    if (data.ok) entry.resolve(data);
    else entry.reject(new Error(data.error ?? 'worker error'));
  }

  private request(msg: Record<string, unknown>, transfer: Transferable[] = []): Promise<unknown> {
    if (!this.worker) return Promise.reject(new Error('worker not running'));
    const id = ++this.reqId;
    return new Promise((resolve, reject) => {
      this.pending.set(id, { resolve: resolve as (v: unknown) => void, reject });
      this.worker!.postMessage({ ...msg, reqId: id }, transfer);
    });
  }
}
